import type { Pt } from "./cable-routing";
import type { Trunk } from "./cable-trunk";
import { GEOMETRY } from "./canvas-geometry";

const { LANE_SPACING } = GEOMETRY;

/**
 * Clusters of parallel cable segments, fed back into pass-2 as corridors. Their
 * `zoneId` is `"density:<n>"` — no zone of that id exists.
 */

const MIN_CLUSTER_SIZE = 3;
const MIN_TRUNK_LENGTH = 100;
const MERGE_DISTANCE = 1.5 * LANE_SPACING;

type SegRef = {
  cableId: string;
  orientation: "h" | "v";
  /** Perpendicular coord: y for horizontal segments, x for vertical. */
  coord: number;
  /** Projection range along the segment's own axis. */
  lo: number;
  hi: number;
};

function collectInteriorSegments(paths: Map<string, Pt[]>): SegRef[] {
  const out: SegRef[] = [];
  for (const [cableId, path] of paths) {
    const numSegs = path.length - 1;
    // Skip the first and last segments: pinned to terminals, so not free to move.
    for (let i = 1; i < numSegs - 1; i++) {
      const a = path[i];
      const b = path[i + 1];
      if (a.x === b.x && a.y !== b.y) {
        out.push({
          cableId,
          orientation: "v",
          coord: a.x,
          lo: Math.min(a.y, b.y),
          hi: Math.max(a.y, b.y),
        });
      } else if (a.y === b.y && a.x !== b.x) {
        out.push({
          cableId,
          orientation: "h",
          coord: a.y,
          lo: Math.min(a.x, b.x),
          hi: Math.max(a.x, b.x),
        });
      }
    }
  }
  return out;
}

function clusterByCoord(segs: SegRef[]): SegRef[][] {
  const sorted = [...segs].sort((a, b) => a.coord - b.coord);
  const clusters: SegRef[][] = [];
  let cur: SegRef[] = [];
  let lastCoord = Number.NEGATIVE_INFINITY;
  for (const s of sorted) {
    if (cur.length > 0 && s.coord - lastCoord > MERGE_DISTANCE) {
      clusters.push(cur);
      cur = [];
    }
    cur.push(s);
    lastCoord = s.coord;
  }
  if (cur.length > 0) clusters.push(cur);
  return clusters;
}

function clusterByProjection(segs: SegRef[]): SegRef[][] {
  const sorted = [...segs].sort((a, b) => a.lo - b.lo);
  const clusters: SegRef[][] = [];
  let cur: SegRef[] = [];
  let curHi = Number.NEGATIVE_INFINITY;
  for (const s of sorted) {
    if (cur.length > 0 && s.lo > curHi) {
      clusters.push(cur);
      cur = [];
      curHi = Number.NEGATIVE_INFINITY;
    }
    cur.push(s);
    if (s.hi > curHi) curHi = s.hi;
  }
  if (cur.length > 0) clusters.push(cur);
  return clusters;
}

function trunkFromCluster(
  cluster: SegRef[],
  orientation: "h" | "v",
  id: number,
): Trunk | null {
  const lo = Math.min(...cluster.map((s) => s.lo));
  const hi = Math.max(...cluster.map((s) => s.hi));
  if (hi - lo < MIN_TRUNK_LENGTH) return null;
  // Median, not mean: one far member must not drag the trunk off the cluster.
  const coords = cluster.map((s) => s.coord).sort((a, b) => a - b);
  const median = coords[Math.floor(coords.length / 2)];
  const polyline: Pt[] =
    orientation === "h"
      ? [
          { x: lo, y: median },
          { x: hi, y: median },
        ]
      : [
          { x: median, y: lo },
          { x: median, y: hi },
        ];
  const bbox =
    orientation === "h"
      ? {
          x: lo,
          y: median - LANE_SPACING,
          w: hi - lo,
          h: 2 * LANE_SPACING,
        }
      : {
          x: median - LANE_SPACING,
          y: lo,
          w: 2 * LANE_SPACING,
          h: hi - lo,
        };
  return {
    zoneId: `density:${id}`,
    polyline,
    orientation,
    bbox,
  };
}

export function extractDensityTrunks(paths: Map<string, Pt[]>): Trunk[] {
  const segs = collectInteriorSegments(paths);
  if (segs.length < MIN_CLUSTER_SIZE) return [];

  const trunks: Trunk[] = [];
  let id = 0;
  for (const orientation of ["h", "v"] as const) {
    const sameAxis = segs.filter((s) => s.orientation === orientation);
    const coordClusters = clusterByCoord(sameAxis);
    for (const cc of coordClusters) {
      if (cc.length < MIN_CLUSTER_SIZE) continue;
      const projClusters = clusterByProjection(cc);
      for (const pc of projClusters) {
        // Distinct cables, not segments: one cable zig-zagging the band is a detour.
        if (new Set(pc.map((s) => s.cableId)).size < MIN_CLUSTER_SIZE) continue;
        const trunk = trunkFromCluster(pc, orientation, id);
        if (trunk) {
          trunks.push(trunk);
          id += 1;
        }
      }
    }
  }
  return trunks;
}
